import {isRTL} from '../../../../../Localization';
import {keys} from '../../../../../constants/KeyCodes';
import {Form} from '../../../../../stores/Form';
import {NavParams} from '../../Table/types/NavParams';
import {
    calculateFocusRowIndex,
    changePaginationPage,
    getNextCellIndex,
    getPrevCellIndex,
    NavDirection,
    navigateToNextCell,
    shouldChangePage
} from './navigation-helpers';

export const handleNavigation = async (e: KeyboardEvent, form: Form, data: NavParams) => {
    const rtl = isRTL();
    const {
        columns,
        currentCellIndex,
        rowsCount,
        currentRow,
        setFocusCell,
        screenRows: visibleRows,
        searchModeLevel
    } = data;
    const isShiftPress = e.shiftKey;
    const currentColumn = columns[currentCellIndex];

    switch (e.key) {
        case keys.ARROW_LEFT:
            if (currentColumn.editMode || e.altKey) {
                return;
            }
            e.preventDefault();
            const leftCellIndex = rtl
                ? getNextCellIndex(currentCellIndex, columns)
                : getPrevCellIndex(currentCellIndex, columns);
            setFocusCell(leftCellIndex, currentRow);
            break;
        case keys.ARROW_RIGHT:
            if (currentColumn.editMode || e.altKey) {
                return;
            }
            e.preventDefault();

            const rightCellIndex = rtl
                ? getPrevCellIndex(currentCellIndex, columns)
                : getNextCellIndex(currentCellIndex, columns);
            setFocusCell(rightCellIndex, currentRow);
            break;

        case keys.ARROW_UP:
            e.preventDefault();

            if (shouldChangePage(form, currentRow, NavDirection.prev)) {
                const focusRow = calculateFocusRowIndex(form, NavDirection.prev);
                await changePaginationPage(form, NavDirection.prev, focusRow);
            } else {
                const upRowIndex = currentRow > 0 ? currentRow - 1 : rowsCount - 1;
                setFocusCell(currentCellIndex, upRowIndex);
            }
            break;

        case keys.ARROW_DOWN:
            e.preventDefault();

            if (shouldChangePage(form, currentRow, NavDirection.next)) {
                await changePaginationPage(form, NavDirection.next, 0);
            } else {
                const lastRowIndex = rowsCount - 1;
                const downRowIndex = currentRow < lastRowIndex ? currentRow + 1 : 0;
                if (rowsCount === 1) {
                    /** This is a special case for the empty form state.
                     * If the user fills the empty record, we allow the Grid component
                     * to trigger the validation callback (`validateFocusChange`).
                     * - If validation passes, it saves the record and creates a new one below.
                     * - If validation fails, navigation is prevented, and the current new index is skipped.
                     **/
                    setFocusCell(currentCellIndex, rowsCount);
                } else if (currentRow !== downRowIndex) {
                    setFocusCell(currentCellIndex, downRowIndex);
                }
            }
            break;
        case keys.ENTER:
            e.preventDefault();
            if (searchModeLevel) {
                break;
            }
            navigateToNextCell(data);
            break;
        case keys.TAB:
            e.preventDefault();

            if (isShiftPress) {
                const nextCellIndex = currentCellIndex > 0 ? currentCellIndex - 1 : 0;
                setFocusCell(nextCellIndex, currentRow);
                break;
            }

            navigateToNextCell(data);
            break;

        case keys.PAGE_UP:
            if (shouldChangePage(form, currentRow, NavDirection.prev)) {
                await changePaginationPage(form, NavDirection.prev);
            } else {
                const prevScreenRow = Math.max(currentRow - visibleRows, 0);
                setFocusCell(currentCellIndex, prevScreenRow);
            }
            break;

        case keys.PAGE_DOWN:
            if (shouldChangePage(form, currentRow, NavDirection.next)) {
                await changePaginationPage(form, NavDirection.next, 0);
            } else {
                const lastRowOnPage = rowsCount - 1;
                setFocusCell(currentCellIndex, Math.min(currentRow + visibleRows, lastRowOnPage));
            }
            break;
        default:
            break;
    }
};
